import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  InformationCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import EmailTemplateModal from './EmailTemplateModal';

const EmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [templateTypes, setTemplateTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);


  useEffect(() => {
    fetchTemplates();
    fetchTemplateTypes();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/email-templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to fetch email templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplateTypes = async () => {
    try {
      const response = await api.get('/email-templates/types/list');
      setTemplateTypes(response.data);
    } catch (error) {
      console.error('Error fetching template types:', error);
    }
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setShowModal(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setShowModal(true);
  };

  const handlePreviewTemplate = (template) => {
    setPreviewTemplate(template);
    setShowPreview(true);
  };

  const handleDuplicateTemplate = async (template) => {
    try {
      const response = await api.post(`/email-templates/${template._id}/duplicate`);
      toast.success('Template duplicated successfully');
      fetchTemplates();
    } catch (error) {
      console.error('Error duplicating template:', error);
      toast.error('Failed to duplicate template');
    }
  };

  const handleSetDefault = async (template) => {
    try {
      await api.patch(`/email-templates/${template._id}/set-default`);
      toast.success('Template set as default successfully');
      fetchTemplates();
    } catch (error) {
      console.error('Error setting default template:', error);
      toast.error('Failed to set default template');
    }
  };

  const handleDeleteTemplate = async (template) => {
    if (!window.confirm(`Are you sure you want to delete "${template.name}"?`)) {
      return;
    }

    try {
      await api.delete(`/email-templates/${template._id}`);
      toast.success('Template deleted successfully');
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error(error.response?.data?.message || 'Failed to delete template');
    }
  };

  const handleSubmit = async (formData) => {
    try {
      if (editingTemplate) {
        await api.put(`/email-templates/${editingTemplate._id}`, formData);
        toast.success('Template updated successfully');
      } else {
        await api.post('/email-templates', formData);
        toast.success('Template created successfully');
      }
      
      setShowModal(false);
      setEditingTemplate(null);
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error(error.response?.data?.message || 'Failed to save template');
    }
  };



  const getTypeLabel = (type) => {
    const typeObj = templateTypes.find(t => t.value === type);
    return typeObj?.label || type;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Templates</h1>
          <p className="text-gray-600">Manage and customize email templates for your system</p>
        </div>
        <button
          onClick={handleCreateTemplate}
          className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center space-x-2"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Create Template</span>
        </button>
      </div>

      {/* Instructions Panel */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <InformationCircleIcon className="h-6 w-6 text-blue-600 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">Email Template Instructions</h3>
            <div className="text-sm text-blue-800 space-y-2">
              <p><strong>Variable Usage:</strong> Use double curly braces to insert variables: <code className="bg-blue-100 px-1 rounded">{`{{variableName}}`}</code></p>
              <p><strong>HTML Support:</strong> Templates support HTML formatting for rich email content.</p>
              <p><strong>Variable Categories:</strong> Variables are organized by category (User, Form, Client, Product, etc.) for easy access.</p>
              <p><strong>Custom Variables:</strong> You can add custom variables specific to your template needs.</p>
              <p><strong>Iterators & Loops:</strong> Use <code className="bg-blue-100 px-1 rounded">{`{{#each arrayName}}...{{/each}}`}</code> to loop through arrays.</p>
              <p><strong>Conditionals:</strong> Use <code className="bg-blue-100 px-1 rounded">{`{{#if condition}}...{{/if}}`}</code> for conditional content.</p>
              
              <details className="mt-3">
                <summary className="cursor-pointer font-semibold text-blue-900">📋 Example Template with Iterators</summary>
                <div className="mt-2 p-3 bg-blue-50 rounded text-xs">
                  <pre className="whitespace-pre-wrap text-blue-800">
{`Dear {{userName}},

Your form submission ({{submissionId}}) has been received.

**Products Requested:**
{{#each products}}
{{@index}}. {{productName}} - {{totalFees}}
{{/each}}

**Required Documents:**
{{#if documents}}
{{#each documents}}
- {{documentName}} {{#unless isUploaded}}(Not uploaded){{/unless}}
{{/each}}
{{else}}
No documents required for this submission.
{{/if}}

**Contact Persons:**
{{#each contactPersons}}
{{@index}}. {{name}} - {{email}} - {{phone}}
{{/each}}

**Fees Breakdown:**
{{#each fees}}
{{@index}}. {{feeType}}: {{amount}}
{{/each}}

Total Amount: {{totalAmount}}

Best regards,
{{companyName}}`}
                  </pre>
                </div>
              </details>
              
              <details className="mt-2">
                <summary className="cursor-pointer font-semibold text-blue-900">🔄 More Iterator Examples</summary>
                <div className="mt-2 p-3 bg-blue-50 rounded text-xs space-y-3">
                  <div>
                    <strong>Simple List:</strong>
                    <pre className="whitespace-pre-wrap text-blue-800 mt-1">
{`{{#each items}}
• {{itemName}}
{{/each}}`}
                    </pre>
                  </div>
                  <div>
                    <strong>Numbered List with Index:</strong>
                    <pre className="whitespace-pre-wrap text-blue-800 mt-1">
{`{{#each items}}
{{@index}}. {{itemName}} - {{itemValue}}
{{/each}}`}
                    </pre>
                  </div>
                  <div>
                    <strong>Conditional with First/Last:</strong>
                    <pre className="whitespace-pre-wrap text-blue-800 mt-1">
{`{{#each items}}
{{#if @first}}📋 {{/if}}{{itemName}}{{#if @last}} ✅{{/if}}
{{/each}}`}
                    </pre>
                  </div>
                  <div>
                    <strong>Nested Loops:</strong>
                    <pre className="whitespace-pre-wrap text-blue-800 mt-1">
{`{{#each categories}}
**{{categoryName}}:**
{{#each products}}
  - {{productName}} ({{price}})
{{/each}}
{{/each}}`}
                    </pre>
                  </div>
                </div>
              </details>
            </div>
          </div>
          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="text-blue-600 hover:text-blue-800"
          >
            {showInstructions ? 'Hide Details' : 'Show Details'}
          </button>
        </div>
        
        {showInstructions && (
          <div className="mt-4 pt-4 border-t border-blue-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-semibold text-blue-900 mb-2">Variable Categories</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li><strong>User:</strong> User-specific information (name, email, phone)</li>
                  <li><strong>Form:</strong> Form submission details (name, ID, status)</li>
                  <li><strong>Client:</strong> Client information (name, email, address)</li>
                  <li><strong>Product:</strong> Product details and fees</li>
                  <li><strong>Invoice:</strong> Invoice-related information</li>
                  <li><strong>System:</strong> Company and system information</li>
                  <li><strong>Date/Time:</strong> Various date and time variables</li>
                  <li><strong>Verification:</strong> Verification codes and links</li>
                </ul>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-blue-900 mb-2">Best Practices</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Always test templates with sample data</li>
                  <li>• Use clear, professional language</li>
                  <li>• Include a clear call-to-action when needed</li>
                  <li>• Keep subject lines concise and relevant</li>
                  <li>• Use variables to personalize content</li>
                  <li>• Preview templates before saving</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Templates List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => (
          <div key={template._id} className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                  <p className="text-sm text-gray-500">{getTypeLabel(template.type)}</p>
                </div>
                <div className="flex items-center space-x-1">
                  {template.isDefault && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Default
                    </span>
                  )}
                  {!template.isActive && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      Inactive
                    </span>
                  )}
                </div>
              </div>
              
              <p className="text-sm text-gray-600 mb-4 line-clamp-2">{template.subject}</p>
              
              {template.description && (
                <p className="text-sm text-gray-500 mb-4 line-clamp-2">{template.description}</p>
              )}

              <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                <span>Created by: {template.createdBy?.firstName} {template.createdBy?.lastName}</span>
                <span>{new Date(template.createdAt).toLocaleDateString()}</span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEditTemplate(template)}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                    title="Edit template"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handlePreviewTemplate(template)}
                    className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors"
                    title="Preview template"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDuplicateTemplate(template)}
                    className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-md transition-colors"
                    title="Duplicate template"
                  >
                    <DocumentDuplicateIcon className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex space-x-2">
                  {!template.isDefault && (
                    <button
                      onClick={() => handleSetDefault(template)}
                      className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                    >
                      Set Default
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteTemplate(template)}
                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    title="Delete template"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit Modal */}
      <EmailTemplateModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        template={editingTemplate}
        onSave={handleSubmit}
        templateTypes={templateTypes}
        onPreview={handlePreviewTemplate}
      />

      {/* Preview Modal */}
      {showPreview && previewTemplate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium text-gray-900">Template Preview</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-700">Subject:</h4>
                <p className="text-lg font-semibold">{previewTemplate.subject}</p>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Content:</h4>
                <div 
                  className="border border-gray-300 rounded-md p-4 bg-gray-50"
                  dangerouslySetInnerHTML={{ __html: previewTemplate.content }}
                />
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => setShowPreview(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplates;
